import Switch from '@shared/components/Switch/Switch';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as NotificationIcon } from 'assets/icons/Notification.svg';
import useToast from 'contexts/ToastProvider/useToast';
import { useWebPushContext } from 'contexts/WebPushContext/WebPushContext';
import useCookies from 'hooks/useCookies';
import useIsFeatureEnabled from 'hooks/useIsFeatureEnabled';
import useSoundSettings from 'hooks/useSoundSettings/useSoundSettings';
import SettingsCard from 'modules/SettingsModal/SettingsCard/SettingsCard';
import { FeatureFlag, matrixConstants } from 'utils/Constants';
import logger from 'utils/logging/faro';
import testingConstants from 'utils/testingConstants';

import PushNotificationsRow from './PushNotificationsRow';

/**
 * Notifications settings tab — renders browser push and (when messenger is
 * enabled) notification sound toggles inside a single "How to notify you?" card.
 */
const NotificationsTab = () => {
  const { t } = useTranslation();
  const [mxLoggedIn] = useCookies<boolean>(matrixConstants.MX_LOGGED_IN, false);
  const isMessengerEnabled = useIsFeatureEnabled(FeatureFlag.FE_ENABLE_MESSENGER);
  const { isPushSupported } = useWebPushContext();
  const showSoundToggle = mxLoggedIn && isMessengerEnabled;

  const { isMuted, setMuted } = useSoundSettings();
  const { pushToast } = useToast();
  const [isSavingSound, setIsSavingSound] = useState(false);

  const handleToggleSound = useCallback(
    async (enabled: boolean) => {
      if (isSavingSound) return;
      setIsSavingSound(true);
      try {
        await setMuted(!enabled);
      } catch (error) {
        logger.error('NotificationsTab: failed to update sound setting', error);
        pushToast({ variant: 'error', title: t('messenger.sound_mute_failed') });
      } finally {
        setIsSavingSound(false);
      }
    },
    [isSavingSound, setMuted, pushToast, t]
  );

  return (
    <div className="flex flex-col gap-6 items-start w-full pt-6 pb-4 px-4 bg-gray-10">
      <span className="text-xl font-semibold">{t('notifications_label')}</span>
      <SettingsCard>
        <div className="w-full h-full p-2">
          <SettingsCard.Header icon={NotificationIcon} title={t('notifications.how_to_notify')} />
          <SettingsCard.Content>
            <PushNotificationsRow />
            {showSoundToggle && (
              <>
                {isPushSupported && <hr className="border-grayscale-gray30 my-1" />}
                <div className="flex gap-4 items-start w-full py-1">
                  <div className="flex flex-col gap-1 flex-1 min-w-0">
                    <SettingsCard.Content.Title>
                      {t('messenger.notifications_tab_sound_title')}
                    </SettingsCard.Content.Title>
                    <SettingsCard.Content.Description>
                      {t('messenger.notifications_tab_sound_description')}
                    </SettingsCard.Content.Description>
                  </div>
                  <Switch
                    checked={!isMuted}
                    onChange={handleToggleSound}
                    isLoading={isSavingSound}
                    dataTestId={testingConstants.notificationsTabSoundToggle}
                  />
                </div>
              </>
            )}
          </SettingsCard.Content>
        </div>
      </SettingsCard>
    </div>
  );
};

export default NotificationsTab;
